import React, { useState } from "react";
import { useCountries } from "../../context/CountryContext";

import { useNavigate } from "react-router-dom";
import api from "../../config/api";
import "./Register.css";
import PhoneInput from "../Common/PhoneInput";
import {
  DEFAULT_PHONE_COUNTRY,
  buildPhonePayload,
  isPhoneComplete,
  formatPhonePreview,
} from "../../utils/phone";

const Register = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const { countries } = useCountries();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: {
      countryCode: DEFAULT_PHONE_COUNTRY,
      nationalNumber: "",
    },
    password: "",
    confirmPassword: ""
  });

  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setError("");
  };

  // Validation
  const validateForm = () => {
    if (!form.firstName.trim()) {
      setError("First name is required");
      return false;
    }
    if (!form.lastName.trim()) {
      setError("Last name is required");
      return false;
    }
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) {
      setError("Valid email is required");
      return false;
    }
    if (!isPhoneComplete(form.phone, countries)) {
      setError("Valid mobile number is required");
      return false;
    }
    if (!form.password.trim() || form.password.length < 6) {
      setError("Password must be at least 6 characters");
      return false;
    }
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    if (!termsAccepted) {
      setError("You must agree to the Terms & Conditions and Privacy Policy");
      return false;
    }
    return true;
  };

  // STEP 1 → Register Initiate
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      await api.post("/auth/register/initiate", {
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        ...buildPhonePayload(form.phone),
        password: form.password
      });

      setError("");
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // STEP 2 → OTP Verify
  const handleVerify = async (e) => {
    e.preventDefault();

    if (!otp.trim()) {
      setError("OTP is required");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/register/verify", {
        ...buildPhonePayload(form.phone),
        otp
      });

      setError("");
      setShowSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || "OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-card">
        {showSuccess ? (
          <div className="register-form success-step" style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div className="success-icon-container">
              <svg viewBox="0 0 24 24" className="success-svg" style={{ width: '80px', height: '80px', color: '#4CAF50', marginBottom: '20px' }}>
                <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <h2 className="success-title">Account Created!</h2>
            <p className="success-text">
              Your registration is complete. You can now log in to start ordering your favorite meals.
            </p>
            <button className="submit-btn" onClick={() => navigate("/login")} style={{ marginTop: '30px' }}>
              Go to Login
            </button>
          </div>
        ) : (
          <>
            {/* HEADER SECTION */}
            <div className="register-header" style={{ position: 'relative' }}>
              <button onClick={() => navigate(-1)} className="back-btn" style={{ position: 'absolute', top: '20px', left: '20px', background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', padding: '8px', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <div className="register-header-content">
                <h1>Create Your Account</h1>
                <p>Sign up to explore delicious meals and get them delivered to your location</p>
              </div>
            </div>

            {/* FORM SECTION */}
            <div className="register-form">
              {step === 1 ? (
                <form onSubmit={handleRegister}>
                  {/* FIRST NAME & LAST NAME */}
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">First Name</label>
                      <input
                        type="text"
                        name="firstName"
                        placeholder="Enter First Name"
                        value={form.firstName}
                        onChange={handleChange}
                        className="form-input"
                        disabled={loading}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Last Name</label>
                      <input
                        type="text"
                        name="lastName"
                        placeholder="Enter Last Name"
                        value={form.lastName}
                        onChange={handleChange}
                        className="form-input"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* EMAIL */}
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter your email"
                      value={form.email}
                      onChange={handleChange}
                      className="form-input"
                      disabled={loading}
                    />
                  </div>

                  {/* MOBILE NUMBER */}
                  <div className="form-group">
                    <PhoneInput
                      label="Mobile Number"
                      value={form.phone}
                      onChange={(phone) => {
                        setForm({ ...form, phone });
                        setError("");
                      }}
                      disabled={loading}
                      required
                      wrapperStyle={{ marginBottom: 0 }}
                    />
                  </div>

                  {/* PASSWORD */}
                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <div className="password-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="Enter your password"
                        value={form.password}
                        onChange={handleChange}
                        className="form-input"
                        disabled={loading}
                        style={{ paddingRight: "40px" }}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={loading}
                      >
                        {showPassword ? (
                          <svg fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 5a7 7 0 017 7 7 7 0 01-7 7 7 7 0 01-7-7 7 7 0 017-7m0-2a9 9 0 109 9 9 9 0 00-9-9z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        ) : (
                          <svg fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3.98 8.223A10.477 10.477 0 001.934 12c2.292 5.558 7.306 9 12.066 9 .996 0 1.972-.092 2.926-.27l-2.655-2.656A5.002 5.002 0 0012 17c-2.757 0-5-2.243-5-5 0-1.289.487-2.461 1.288-3.354l-2.308-2.309zm19.27 2.097a10.472 10.472 0 00-1.986-2.89l-2.654 2.655A5.002 5.002 0 0112 7c2.757 0 5 2.243 5 5 0 1.289-.487 2.461-1.288 3.354l2.308 2.309A10.476 10.476 0 0023.066 12c-2.292-5.558-7.306-9-12.066-9z"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* CONFIRM PASSWORD */}
                  <div className="form-group">
                    <label className="form-label">Confirm Password</label>
                    <div className="password-wrapper">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        placeholder="Confirm your password"
                        value={form.confirmPassword}
                        onChange={handleChange}
                        className="form-input"
                        disabled={loading}
                        style={{ paddingRight: "40px" }}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        disabled={loading}
                      >
                        {showConfirmPassword ? (
                          <svg fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 5a7 7 0 017 7 7 7 0 01-7 7 7 7 0 01-7-7 7 7 0 017-7m0-2a9 9 0 109 9 9 9 0 00-9-9z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        ) : (
                          <svg fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3.98 8.223A10.477 10.477 0 001.934 12c2.292 5.558 7.306 9 12.066 9 .996 0 1.972-.092 2.926-.27l-2.655-2.656A5.002 5.002 0 0012 17c-2.757 0-5-2.243-5-5 0-1.289.487-2.461 1.288-3.354l-2.308-2.309zm19.27 2.097a10.472 10.472 0 00-1.986-2.89l-2.654 2.655A5.002 5.002 0 0112 7c2.757 0 5 2.243 5 5 0 1.289-.487 2.461-1.288 3.354l2.308 2.309A10.476 10.476 0 0023.066 12c-2.292-5.558-7.306-9-12.066-9z"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* TERMS AGREEMENT */}
                  <div className="form-group">
                    <label className="terms-checkbox-label">
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => {
                          setTermsAccepted(e.target.checked);
                          setError("");
                        }}
                        disabled={loading}
                      />
                      <span>
                        I agree to the{" "}
                        <a
                          href="/terms"
                          className="terms-link"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate("/terms");
                          }}
                        >
                          Terms & Conditions
                        </a>{" "}
                        and{" "}
                        <a
                          href="/privacy-policy"
                          className="terms-link"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate("/privacy-policy");
                          }}
                        >
                          Privacy Policy
                        </a>
                      </span>
                    </label>
                  </div>

                  {/* ERROR MESSAGE */}
                  {error && <div className="error-message">{error}</div>}

                  {/* SUBMIT BUTTON */}
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={loading}
                  >
                    {loading ? "Creating Account..." : "Create Account"}
                  </button>

                  {/* FOOTER */}
                  <div className="register-footer">
                    <p className="login-prompt">
                      Already have an account?
                      <a
                        href="/login"
                        className="login-link"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate("/login");
                        }}
                      >
                        Login
                      </a>
                    </p>
                  </div>
                </form>
              ) : (
                /* OTP VERIFICATION STEP */
                <form onSubmit={handleVerify}>
                  <div className="otp-section">
                    <h2>Verify Your Email</h2>
                    <p>Enter the OTP sent to {formatPhonePreview(form.phone, countries)}</p>

                    <div className="form-group">
                      <label className="form-label">Enter OTP</label>
                      <input
                        type="text"
                        placeholder="Enter 6-digit OTP"
                        value={otp}
                        onChange={(e) => {
                          setOtp(e.target.value);
                          setError("");
                        }}
                        className="form-input"
                        maxLength="6"
                        disabled={loading}
                      />
                    </div>

                    {error && <div className="error-message">{error}</div>}

                    <button
                      type="submit"
                      className="submit-btn"
                      disabled={loading}
                    >
                      {loading ? "Verifying..." : "Verify OTP"}
                    </button>

                    <p className="resend-text" style={{ textAlign: 'center', marginTop: '20px' }}>
                      Didn't receive OTP?{" "}
                      <span
                        className="resend-link"
                        style={{ color: '#E31E24', cursor: 'pointer', fontWeight: 'bold' }}
                        onClick={() => setStep(1)}
                      >
                        Go Back
                      </span>
                    </p>
                  </div>
                </form>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Register;
